import ScrollReveal from "@/components/base/ScrollReveal";
import { Link } from "react-router-dom";

export default function WhyStructureSection() {
  const points = [
    {
      icon: "ri-briefcase-line",
      title: "Value-Centered Approach",
      desc: "Focus on meaningful business or mission needs that drive real organizational value from AI adoption.",
    },
    {
      icon: "ri-loop-left-line",
      title: "Iterative Progress",
      desc: "Start with practical steps and AI use cases, learn quickly, and build momentum through measured, accountable progress.",
    },
    {
      icon: "ri-team-line",
      title: "Multidisciplinary Involvement",
      desc: "Bring together the right stakeholders — strategy, operations, HR, workforce, policy, legal, privacy, security, data, and technology — for aligned outcomes.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Governance That Enables",
      desc: "Establish governance mechanisms that support responsible AI use without blocking innovation.",
    },
  ];

  const risks = [
    "Scattered efforts with unclear direction",
    "Low adoption despite AI investment",
    "Unmanaged risk and compliance exposure",
    "Unclear ownership and accountability",
    "Disconnected or duplicative AI initiatives across the enterprise",
  ];

  return (
    <section id="why-structure" className="relative bg-bg-section py-20 md:py-28 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="absolute top-1/3 -left-32 w-[300px] h-[300px] bg-primary/4 rounded-full blur-[100px]" />
      <div className="absolute bottom-1/4 -right-32 w-[300px] h-[300px] bg-primary/4 rounded-full blur-[100px]" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-14">
              <span className="font-sans text-[14px] font-medium text-primary tracking-wider uppercase">
                The Foundation for Success
              </span>
              <h2 className="font-serif text-[32px] md:text-[44px] font-semibold text-text-headline leading-tight mt-4 mb-6">
                Why AI Adoption Needs{" "}
                <span className="italic text-primary">Structure</span>
              </h2>
              <p className="font-sans text-[17px] md:text-[19px] text-text-muted leading-relaxed max-w-3xl mx-auto">
                AI adoption succeeds when organizations take a value-centered, iterative, and multidisciplinary approach. That means focusing on meaningful business or mission needs, starting with practical AI use cases, leveraging successes to expand complexity, involving the right stakeholders, addressing workforce readiness, and establishing governance that enables innovation and responsible use.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
            {points.map((p, i) => (
              <ScrollReveal key={i} delay={i * 100} direction="up">
                <div className="bg-bg-card rounded-2xl p-7 md:p-8 border border-border transition-all duration-300 hover:border-primary/40 hover:-translate-y-1 group h-full">
                  <div className="w-11 h-11 bg-primary-tint rounded-full flex items-center justify-center mb-5 transition-all duration-300 group-hover:bg-primary/15">
                    <i className={`${p.icon} text-primary text-lg w-5 h-5 flex items-center justify-center`} />
                  </div>
                  <h3 className="font-sans text-[17px] md:text-[18px] font-semibold text-text-headline mb-2 group-hover:text-primary transition-colors duration-300">
                    {p.title}
                  </h3>
                  <p className="font-sans text-[14px] text-text-muted leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={200}>
            <div className="bg-bg-card rounded-2xl p-8 md:p-10 border border-border relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div>
                  <h3 className="font-serif text-[22px] md:text-[26px] font-semibold text-text-headline mb-4">
                    Without Structure, Organizations Risk:
                  </h3>
                  <ul className="flex flex-col gap-3">
                    {risks.map((r, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-primary-tint rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-error-warning-line text-primary text-sm w-4 h-4 flex items-center justify-center" />
                        </div>
                        <span className="font-sans text-[15px] text-text-body leading-relaxed">
                          {r}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-col gap-6">
                  <div className="relative group rounded-2xl overflow-hidden shadow-2xl bg-bg-card">
                    <img
                      src="https://storage.readdy-site.link/project_files/d2cadc96-e8bf-4a0a-9bca-6276c5c079f1/599b5587-bcd7-441b-a9c2-5fb321ee0ec9_woman-presenting-at-podium.png?v=8940630a6df6faaaef3cc19bcdb4b284"
                      alt="AI strategy advisor presenting structured adoption framework and governance principles to audience at professional conference"
                      className="w-full h-auto object-contain object-center"
                    />
                  </div>
                  <div className="flex flex-col items-start gap-4">
                    <p className="font-sans text-[16px] text-text-muted leading-[1.7]">
                      VantageSight helps organizations create the strategy, governance, roadmap, and readiness needed to move forward responsibly.
                    </p>
                    <Link
                      to="/contact"
                      className="font-sans text-[16px] font-medium text-white bg-primary hover:bg-primary-light px-8 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer inline-block"
                    >
                      Book an Introductory Call
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}